// AI 管理助理(admin assistant)資料層。
//
// 兩個後端端點(restful `feat/admin-assistant` 分支同時在建,契約 v1):
//   POST /v3/admin/assistant/chat    — **SSE 串流**。body {messages:[{role,content}], page_context?}
//   GET  /v3/admin/assistant/config  — {key_configured, model, enabled}
//
// 助理只做**對話式導覽 + 唯讀查詢**(零 mutate):它回 `text` 增量、`navigate`(協助跳頁)、
// `tool`(查詢中狀態)三種事件,前端不代它寫任何 admin API。
//
// 認證沿用 console 既有機制(比照 aiOpsApi.aiFetch / AppShell devlog SSE):tokenStore 取
// Bearer、401 → refreshAccessToken 換發後重試一次、config.clientId/clientKey 當
// X-Client-Id/Key(CSRF gate)。config 端點回**裸 JSON**(容忍 `{data}` 信封,兩種都讀)。

import { config } from '../../config';
import { ApiError, refreshAccessToken, tokenStore } from '../../lib/api';

const ASSISTANT_PREFIX = '/v3/admin/assistant';

// ── 契約型別 ──────────────────────────────────────────────────────────────────

/** GET /v3/admin/assistant/config。 */
export interface AssistantConfig {
  /** 後端是否已設定 API key(env `RA_ANTHROPIC_API_KEY`)。UI 只顯示布林狀態、永不見明文。 */
  key_configured: boolean;
  /** 目前使用的模型 id(顯示用)。 */
  model: string;
  /** 助理是否啟用(未設 key 時後端回 false → 前端收起輸入框)。 */
  enabled: boolean;
}

/** 送出的一則對話訊息(chat body 內 messages[])。 */
export interface AssistantChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** 喚起助理時帶入的當前頁脈絡(讓助理知道使用者正在哪個畫面)。 */
export interface AssistantPageContext {
  route: string;
  label?: string;
}

/**
 * SSE 事件(每則為一行 `data: {json}`):
 *   text     — 助理回覆文字增量(streaming append)
 *   navigate — 助理建議跳到某 admin 頁(to=route,label=顯示名,reason=為何)
 *   tool     — 助理正在查詢(name=工具名,status=running/done/…)細粒度進度
 *   done     — 本輪回覆結束
 *   error    — 本輪出錯(code/message)
 */
export type AssistantEvent =
  | { type: 'text'; delta: string }
  | { type: 'navigate'; to: string; label: string; reason?: string }
  | { type: 'tool'; name: string; status: string }
  | { type: 'done' }
  | { type: 'error'; code: string; message: string };

// ── config 端點 ───────────────────────────────────────────────────────────────

function assistantUrl(path: string): string {
  return config.apiBase + ASSISTANT_PREFIX + path;
}

export function baseHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Client-Id': config.clientId,
    'X-Client-Key': config.clientKey,
    ...extra,
  };
  const token = tokenStore.get();
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return headers;
}

/** 從助理端點回應讀出人類可讀錯誤(restful `{error:{code,message}}` 或 FastAPI `{detail}`)。 */
export function readError(status: number, body: unknown, requestId: string | null): ApiError {
  const b = (body ?? {}) as { detail?: unknown; error?: { code?: string; message?: string } };
  if (b.error && (b.error.code || b.error.message)) {
    return new ApiError(status, b.error.code ?? `HTTP_${status}`, b.error.message ?? `HTTP ${status}`, requestId);
  }
  let detail = '';
  if (typeof b.detail === 'string') detail = b.detail;
  else if (b.detail != null) detail = JSON.stringify(b.detail);
  return new ApiError(status, `HTTP_${status}`, detail || `HTTP ${status}`, requestId);
}

/**
 * GET /v3/admin/assistant/config。回**裸 JSON**;容忍 restful `{data}` 信封(兩種都讀)。
 * 401 → refresh-and-retry 一次。
 */
export async function fetchAssistantConfig(signal?: AbortSignal, _retried = false): Promise<AssistantConfig> {
  const res = await fetch(assistantUrl('/config'), { method: 'GET', headers: baseHeaders(), signal });

  if (res.status === 401 && !_retried) {
    try {
      await refreshAccessToken();
    } catch {
      tokenStore.clear();
      throw new ApiError(401, 'UNAUTHORIZED', '登入逾期，請重新登入', res.headers.get('X-Request-Id'));
    }
    return fetchAssistantConfig(signal, true);
  }

  const requestId = res.headers.get('X-Request-Id');
  const body = (await res.json().catch(() => ({}))) as { data?: AssistantConfig } & Partial<AssistantConfig>;

  if (res.status === 401) {
    tokenStore.clear();
    throw new ApiError(401, 'UNAUTHORIZED', '登入逾期，請重新登入', requestId);
  }
  if (!res.ok) throw readError(res.status, body, requestId);

  // 裸 dict 優先;若後端改用 {data} 信封也吃得下。
  const raw = (body.data ?? body) as Partial<AssistantConfig>;
  return {
    key_configured: Boolean(raw.key_configured),
    model: typeof raw.model === 'string' ? raw.model : '',
    enabled: Boolean(raw.enabled),
  };
}

// ── SSE 串流解析(可注入 reader → 便於測試) ──────────────────────────────────

/**
 * 增量 SSE 解析器。餵入解碼後的字串片段(可能被 chunk 切在事件中間),依 SSE 事件邊界
 * (空白行 `\n\n`)切出完整事件,收集每個事件內的 `data:` 行、串接後 JSON.parse,回傳
 * 已解析的 `AssistantEvent[]`。無法解析(非 JSON / 缺 type)的事件安全跳過。
 */
export class SseEventParser {
  private buffer = '';

  /** 餵入一段文字,回傳這次能完整解析出的事件(可能為空)。 */
  push(chunk: string): AssistantEvent[] {
    this.buffer += chunk;
    // 正規化 CRLF,讓事件邊界一律用 \n\n 判斷。
    this.buffer = this.buffer.replace(/\r\n/g, '\n');
    const events: AssistantEvent[] = [];
    let idx: number;
    while ((idx = this.buffer.indexOf('\n\n')) !== -1) {
      const block = this.buffer.slice(0, idx);
      this.buffer = this.buffer.slice(idx + 2);
      const ev = parseSseBlock(block);
      if (ev) events.push(ev);
    }
    return events;
  }

  /** 串流結束時呼叫:把 buffer 內殘餘(未以空白行收尾)的最後一個事件也嘗試解析出來。 */
  flush(): AssistantEvent[] {
    const rest = this.buffer.trim();
    this.buffer = '';
    if (!rest) return [];
    const ev = parseSseBlock(rest);
    return ev ? [ev] : [];
  }
}

/** 解析單一 SSE 事件區塊(多行);收集 `data:` 行值串接後轉 JSON。 */
export function parseSseBlock(block: string): AssistantEvent | null {
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    const trimmed = line.replace(/\r$/, '');
    if (trimmed.startsWith('data:')) {
      // SSE 規範:去掉 `data:` 後一個可選前導空白。
      dataLines.push(trimmed.slice(5).replace(/^ /, ''));
    }
  }
  if (dataLines.length === 0) return null;
  const payload = dataLines.join('\n').trim();
  if (!payload || payload === '[DONE]') return payload === '[DONE]' ? { type: 'done' } : null;
  return coerceEvent(payload);
}

/** 把一段 JSON 文字轉成受檢的 AssistantEvent;形狀不符回 null(安全跳過)。 */
export function coerceEvent(json: string): AssistantEvent | null {
  let obj: unknown;
  try {
    obj = JSON.parse(json);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const e = obj as Record<string, unknown>;
  switch (e.type) {
    case 'text':
      return typeof e.delta === 'string' ? { type: 'text', delta: e.delta } : null;
    case 'navigate':
      return typeof e.to === 'string'
        ? { type: 'navigate', to: e.to, label: typeof e.label === 'string' ? e.label : '', reason: typeof e.reason === 'string' ? e.reason : undefined }
        : null;
    case 'tool':
      return typeof e.name === 'string' ? { type: 'tool', name: e.name, status: typeof e.status === 'string' ? e.status : 'running' } : null;
    case 'done':
      return { type: 'done' };
    case 'error':
      return { type: 'error', code: typeof e.code === 'string' ? e.code : 'ERROR', message: typeof e.message === 'string' ? e.message : '助理發生錯誤' };
    default:
      return null;
  }
}

/** 最小 reader 介面(便於測試注入 mock,不必造真 ReadableStream)。 */
export interface ByteReader {
  read(): Promise<{ done: boolean; value?: Uint8Array | undefined }>;
}

/**
 * 消費一個 byte reader、解碼、逐事件回呼 onEvent。純函式(reader 可注入 mock)。
 * signal.aborted 時提前收工(呼叫端負責 abort fetch)。
 */
export async function readSseStream(reader: ByteReader, onEvent: (ev: AssistantEvent) => void, signal?: AbortSignal): Promise<void> {
  const decoder = new TextDecoder();
  const parser = new SseEventParser();
  for (;;) {
    if (signal?.aborted) return;
    const { done, value } = await reader.read();
    if (value && value.byteLength > 0) {
      for (const ev of parser.push(decoder.decode(value, { stream: true }))) onEvent(ev);
    }
    if (done) {
      for (const ev of parser.flush()) onEvent(ev);
      return;
    }
  }
}

// ── chat 串流端點 ─────────────────────────────────────────────────────────────

export interface StreamChatArgs {
  messages: AssistantChatMessage[];
  pageContext?: AssistantPageContext;
  onEvent: (ev: AssistantEvent) => void;
  signal?: AbortSignal;
}

/**
 * POST /v3/admin/assistant/chat(SSE)。開串流前若 401 先 refresh 重試一次;非 2xx 讀錯誤
 * 信封並以 `{type:'error'}` 事件回報(讓 UI 一致呈現)。回應 body 以 reader 串流交給
 * readSseStream 逐事件解析。呼叫端傳 AbortController.signal 以支援關窗/取消。
 */
export async function streamAssistantChat(args: StreamChatArgs, _retried = false): Promise<void> {
  const { messages, pageContext, onEvent, signal } = args;
  const res = await fetch(assistantUrl('/chat'), {
    method: 'POST',
    headers: baseHeaders({ Accept: 'text/event-stream' }),
    body: JSON.stringify({ messages, page_context: pageContext }),
    signal,
  });

  if (res.status === 401 && !_retried) {
    try {
      await refreshAccessToken();
    } catch {
      tokenStore.clear();
      onEvent({ type: 'error', code: 'UNAUTHORIZED', message: '登入逾期，請重新登入' });
      return;
    }
    return streamAssistantChat(args, true);
  }

  if (!res.ok) {
    const requestId = res.headers.get('X-Request-Id');
    const body = await res.json().catch(() => ({}));
    const err = readError(res.status, body, requestId);
    onEvent({ type: 'error', code: err.code, message: err.message });
    return;
  }

  if (!res.body) {
    onEvent({ type: 'error', code: 'NO_STREAM', message: '助理未回傳串流內容' });
    return;
  }

  const reader = res.body.getReader();
  try {
    await readSseStream(reader, onEvent, signal);
  } catch (err) {
    // AbortError(關窗/取消)不算錯誤,靜默收工。
    if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
    onEvent({ type: 'error', code: 'STREAM_ERROR', message: err instanceof Error ? err.message : '助理串流中斷' });
  }
}
